import { registerSW } from 'virtual:pwa-register';

/** The browser's deferred install prompt, when it offers one. */
export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

export function installPromptAvailable(): boolean {
  return deferred !== null;
}
export function onInstallChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  await deferred.prompt();
  const { outcome } = await deferred.userChoice;
  deferred = null;
  listeners.forEach((l) => l());
  return outcome;
}

/**
 * Service worker caches the app shell only. Updates wait for the user and are
 * never applied while a write could be in progress. ALG-056, ALG-057
 */
export function setupPwa(handlers: {
  onNeedRefresh(apply: () => Promise<void>): void;
  onOfflineReady(): void;
}): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
    listeners.forEach((l) => l());
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    listeners.forEach((l) => l());
  });
  if (!('serviceWorker' in navigator) || import.meta.env.DEV) return;
  const update = registerSW({
    onNeedRefresh: () => handlers.onNeedRefresh(() => update(true)),
    onOfflineReady: () => handlers.onOfflineReady(),
  });
}
